import { Link, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { BrandMark } from '../components/BrandMark';
import { ScrollToTop } from './ScrollToTop';
import { BOT_URL } from './shared';

/**
 * Section ids the nav + footer link to. Order matters — must match
 * the render order in `LandingProduct` so every click scrolls DOWN
 * from the user's current position, never jumps backward past
 * sections the user has already scrolled through.
 *
 * Render order: Hero → Features → ProductTour → How → Security → Fees → FAQ → CTA.
 * ProductTour has no anchor — it's visible while scrolling between
 * Features and How and doesn't need its own nav target.
 */
const NAV_LINKS = ['features', 'how', 'security', 'fees', 'faq'] as const;
const LEGAL_DOCS = ['terms', 'privacy', 'risk'] as const;

/**
 * Chrome for the public marketing site — sticky nav + footer around an
 * <Outlet/>. Pinned to the dark theme via `data-theme="dark"` so the
 * site reads as walrus-navy no matter what the in-app theme toggle is
 * set to (the attribute selector re-defines `--pb-*` for this subtree).
 */
export function SiteLayout() {
  return (
    <div
      data-theme="dark"
      className="flex min-h-screen flex-col bg-tg-bg font-sans text-tg-text"
    >
      <ScrollToTop />
      <SiteNav />
      <main className="flex-1">
        <Outlet />
      </main>
      <SiteFooter />
    </div>
  );
}

function SiteNav() {
  const { t } = useTranslation();
  return (
    <header className="sticky top-0 z-30 border-b border-pb-border-soft bg-tg-bg/80 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-4 sm:gap-4 sm:px-6">
        <Link
          to="/"
          className="pb-focus-ring shrink-0 rounded-pb-sm"
          aria-label="Kit Pod"
        >
          {/* Hide the "Kit Pod" wordmark below sm — at 320–390px the
              full mark + lang toggle + CTA don't fit on one row. */}
          <span className="sm:hidden">
            <BrandMark size="sm" showWordmark={false} />
          </span>
          <span className="hidden sm:inline-flex">
            <BrandMark size="md" />
          </span>
        </Link>
        <nav
          className="hidden items-center gap-7 md:flex"
          aria-label={t('site.nav.aria')}
        >
          {NAV_LINKS.map((id) => (
            <a
              key={id}
              href={`/#${id}`}
              className="text-sm text-tg-hint transition-colors hover:text-tg-text"
            >
              {t(`site.nav.${id}`)}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <LangToggle />
          <Link
            to="/login"
            className="pb-focus-ring rounded-pb-sm px-2 py-2 text-[13px] text-tg-hint transition-colors hover:text-tg-text"
          >
            {t('site.nav.login')}
          </Link>
          <a
            href={BOT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="pb-btn pb-btn-primary pb-focus-ring min-h-0 px-3 py-2.5 text-[13px] sm:px-4"
          >
            {/* Telegram-only short label below sm so the button fits
                the row; full "Open in Telegram" wording on ≥sm. */}
            <span className="sm:hidden">Telegram</span>
            <span className="hidden sm:inline">
              {t('site.nav.open_tg')}
            </span>
          </a>
        </div>
      </div>
    </header>
  );
}

function LangToggle() {
  const { i18n } = useTranslation();
  const active = i18n.language.startsWith('ru') ? 'ru' : 'en';
  const set = (l: 'ru' | 'en') => {
    void i18n.changeLanguage(l);
    try {
      localStorage.setItem('polybot.lang', l);
    } catch {
      /* storage blocked — language still changes for this session */
    }
  };
  return (
    <div className="flex items-center rounded-pb-md border border-pb-border-soft p-0.5">
      {(['ru', 'en'] as const).map((l) => (
        <button
          key={l}
          type="button"
          onClick={() => set(l)}
          className={clsx(
            'pb-focus-ring rounded-pb-sm px-2 py-1 text-xs font-medium uppercase transition-colors',
            active === l ? 'bg-pb-accent-soft text-pb-accent' : 'text-tg-hint',
          )}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

function SiteFooter() {
  const { t } = useTranslation();
  const year = new Date().getFullYear();
  return (
    <footer className="border-t border-pb-border-soft">
      <div className="mx-auto max-w-6xl px-5 py-14 sm:px-6">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          <div className="lg:col-span-2">
            <BrandMark size="md" />
            <p className="mt-3 max-w-xs text-sm leading-relaxed text-tg-hint">
              {t('site.footer.tagline')}
            </p>
          </div>
          <div>
            <p className="pb-eyebrow">{t('site.footer.nav_heading')}</p>
            <ul className="mt-3 space-y-2.5">
              {NAV_LINKS.map((id) => (
                <li key={id}>
                  <a
                    href={`/#${id}`}
                    className="text-sm text-tg-hint transition-colors hover:text-tg-text"
                  >
                    {t(`site.nav.${id}`)}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="pb-eyebrow">{t('site.footer.legal_heading')}</p>
            <ul className="mt-3 space-y-2.5">
              {LEGAL_DOCS.map((d) => (
                <li key={d}>
                  <Link
                    to={`/legal/${d}`}
                    className="text-sm text-tg-hint transition-colors hover:text-tg-text"
                  >
                    {t(`site.footer.${d}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="mt-12 border-t border-pb-border-soft pt-6">
          <p className="max-w-3xl text-xs leading-relaxed text-tg-hint">
            {t('site.footer.risk_note')}
          </p>
          <p className="mt-3 text-xs text-tg-hint">
            © {year} Kit Pod. {t('site.footer.rights')}
          </p>
        </div>
      </div>
    </footer>
  );
}
